import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { Game, Staff, FAQ, ContactMessage, AdminStats } from '../types';
import { Modal } from '../components/Modal';
import {
  Lock,
  Gamepad2,
  Users,
  HelpCircle,
  Mail,
  Plus,
  Trash2,
  Edit2,
  Database,
  LogOut,
  Sparkles,
  ExternalLink,
  Save,
  CheckCircle2,
  AlertCircle,
  Eye,
  KeyRound,
} from 'lucide-react';

export const AdminPage: React.FC = () => {
  const [adminKey, setAdminKey] = useState<string>(() => {
    return localStorage.getItem('gsr_admin_key') || '';
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return Boolean(localStorage.getItem('gsr_admin_key'));
  });

  const [activeTab, setActiveTab] = useState<'overview' | 'games' | 'staff' | 'faqs' | 'contacts'>('overview');
  const [stats, setStats] = useState<AdminStats>({ gamesCount: 0, staffCount: 0, contactsCount: 0, faqsCount: 0 });

  // Data states
  const [games, setGames] = useState<Game[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Modal form states
  const [isGameModalOpen, setIsGameModalOpen] = useState(false);
  const [editingGame, setEditingGame] = useState<Game | null>(null);
  const [gameForm, setGameForm] = useState({
    title: '',
    slug: '',
    description: '',
    genre: 'Action RPG',
    status: 'Released',
    thumbnail_url: '',
    banner_url: '',
    play_url: '',
    download_url: '',
    rating: 4.8,
    tags: '',
    featured: false,
  });

  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [staffForm, setStaffForm] = useState({
    name: '',
    role: '',
    bio: '',
    avatar_url: '',
    twitter: '',
    github: '',
    linkedin: '',
    sort_order: 0,
  });

  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category: 'General',
    sort_order: 0,
  });

  const notify = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminKey.trim()) {
      notify('error', 'Please enter your Admin Secret Key');
      return;
    }
    // Set authenticated state and store
    localStorage.setItem('gsr_admin_key', adminKey.trim());
    setIsAuthenticated(true);
    notify('success', 'Admin session active');
  };

  const handleLogout = () => {
    localStorage.removeItem('gsr_admin_key');
    setIsAuthenticated(false);
    setAdminKey('');
  };

  const loadAllData = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [fetchedGames, fetchedStaff, fetchedFaqs, fetchedContacts] = await Promise.all([
        api.getGames(),
        api.getStaff(),
        api.getFAQs(),
        api.getContacts(adminKey).catch(() => []),
      ]);
      setGames(fetchedGames);
      setStaff(fetchedStaff);
      setFaqs(fetchedFaqs);
      setContacts(fetchedContacts);
      setStats({
        gamesCount: fetchedGames.length,
        staffCount: fetchedStaff.length,
        faqsCount: fetchedFaqs.length,
        contactsCount: fetchedContacts.length,
      });
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated]);

  // Game Handlers
  const openNewGameModal = () => {
    setEditingGame(null);
    setGameForm({
      title: '',
      slug: '',
      description: '',
      genre: 'Action RPG',
      status: 'Released',
      thumbnail_url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800&q=80',
      banner_url: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=1600&q=80',
      play_url: '',
      download_url: '',
      rating: 4.8,
      tags: 'Multiplayer, Cyberpunk, Fast-Paced',
      featured: false,
    });
    setIsGameModalOpen(true);
  };

  const openEditGameModal = (game: Game) => {
    setEditingGame(game);
    setGameForm({
      title: game.title,
      slug: game.slug,
      description: game.description,
      genre: game.genre,
      status: game.status,
      thumbnail_url: game.thumbnail_url,
      banner_url: game.banner_url || '',
      play_url: game.play_url || '',
      download_url: game.download_url || '',
      rating: game.rating,
      tags: game.tags?.join(', ') || '',
      featured: Boolean(game.featured),
    });
    setIsGameModalOpen(true);
  };

  const handleSaveGame = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: Partial<Game> = {
        title: gameForm.title,
        slug: gameForm.slug || gameForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        description: gameForm.description,
        genre: gameForm.genre,
        status: gameForm.status,
        thumbnail_url: gameForm.thumbnail_url,
        banner_url: gameForm.banner_url,
        play_url: gameForm.play_url,
        download_url: gameForm.download_url,
        rating: Number(gameForm.rating),
        tags: gameForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
        featured: gameForm.featured,
      };

      if (editingGame) {
        await api.updateGame(editingGame.id, payload, adminKey);
        notify('success', `Updated "${gameForm.title}"`);
      } else {
        await api.createGame(payload, adminKey);
        notify('success', `Added new game "${gameForm.title}"`);
      }

      setIsGameModalOpen(false);
      loadAllData();
    } catch (err: any) {
      notify('error', err.message || 'Failed to save game');
    }
  };

  const handleDeleteGame = async (game: Game) => {
    if (!window.confirm(`Are you sure you want to delete "${game.title}"?`)) return;
    try {
      await api.deleteGame(game.id, adminKey);
      notify('success', `Deleted "${game.title}"`);
      loadAllData();
    } catch (err: any) {
      notify('error', err.message || 'Failed to delete game');
    }
  };

  // Staff Handlers
  const openNewStaffModal = () => {
    setEditingStaff(null);
    setStaffForm({
      name: '',
      role: '',
      bio: '',
      avatar_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
      twitter: '',
      github: '',
      linkedin: '',
      sort_order: staff.length + 1,
    });
    setIsStaffModalOpen(true);
  };

  const openEditStaffModal = (member: Staff) => {
    setEditingStaff(member);
    setStaffForm({
      name: member.name,
      role: member.role,
      bio: member.bio || '',
      avatar_url: member.avatar_url,
      twitter: member.twitter || '',
      github: member.github || '',
      linkedin: member.linkedin || '',
      sort_order: member.sort_order || 0,
    });
    setIsStaffModalOpen(true);
  };

  const handleSaveStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await api.updateStaff(editingStaff.id, staffForm, adminKey);
        notify('success', `Updated staff member "${staffForm.name}"`);
      } else {
        await api.createStaff(staffForm, adminKey);
        notify('success', `Added staff member "${staffForm.name}"`);
      }
      setIsStaffModalOpen(false);
      loadAllData();
    } catch (err: any) {
      notify('error', err.message || 'Failed to save staff member');
    }
  };

  const handleDeleteStaff = async (member: Staff) => {
    if (!window.confirm(`Delete ${member.name}?`)) return;
    try {
      await api.deleteStaff(member.id, adminKey);
      notify('success', `Deleted ${member.name}`);
      loadAllData();
    } catch (err: any) {
      notify('error', err.message || 'Failed to delete staff member');
    }
  };

  // FAQ Handlers
  const handleSaveFaq = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createFAQ(faqForm, adminKey);
      notify('success', 'FAQ question created');
      setIsFaqModalOpen(false);
      setFaqForm({ question: '', answer: '', category: 'General', sort_order: 0 });
      loadAllData();
    } catch (err: any) {
      notify('error', err.message || 'Failed to create FAQ');
    }
  };

  const handleDeleteFaq = async (faq: FAQ) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.deleteFAQ(faq.id, adminKey);
      notify('success', 'FAQ removed');
      loadAllData();
    } catch (err: any) {
      notify('error', err.message || 'Failed to delete FAQ');
    }
  };

  const handleInitDB = async () => {
    try {
      await api.initDB(adminKey);
      notify('success', 'Cloudflare D1 tables initialized/verified');
      loadAllData();
    } catch (err: any) {
      notify('error', err.message || 'Error running DB initialization');
    }
  };

  // LOGIN GATE
  if (!isAuthenticated) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-slate-900/80 rounded-3xl border border-slate-800 p-8 shadow-2xl shadow-cyan-500/10 space-y-6">
          <div className="text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-cyan-500 to-blue-600 mx-auto flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Lock className="w-7 h-7 text-slate-950" />
            </div>
            <h1 className="text-2xl font-black text-white">GSR Admin Portal</h1>
            <p className="text-slate-400 text-xs">
              Enter your Studio Admin Secret Key to manage games, staff, and content.
            </p>
          </div>

          {feedback && (
            <div
              className={`p-3.5 rounded-xl text-xs flex items-center gap-2 border ${
                feedback.type === 'success'
                  ? 'bg-emerald-950/60 border-emerald-500/30 text-emerald-300'
                  : 'bg-rose-950/60 border-rose-500/30 text-rose-300'
              }`}
            >
              {feedback.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0" />
              )}
              <span>{feedback.message}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">Admin Secret Key</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  required
                  placeholder="Enter secret key (default: gsradmin2026)"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition font-mono"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 font-bold text-sm shadow-lg shadow-cyan-500/20 transition cursor-pointer"
            >
              Authenticate Session
            </button>
          </form>

          <p className="text-center text-[11px] text-slate-500">
            Default local key: <code className="text-cyan-400 font-mono">gsradmin2026</code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10 pb-24">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-slate-900/60 rounded-3xl border border-slate-800 p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
            <Database className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <span>GSR STUDIO Management Core</span>
              <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-emerald-950 text-emerald-400 border border-emerald-800">
                Cloudflare D1
              </span>
            </h1>
            <p className="text-slate-400 text-xs">
              Live Edge administration for games, staff profiles, FAQs & transmissions.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={handleInitDB}
            className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-cyan-300 text-xs font-semibold border border-slate-700 flex items-center gap-1.5 transition"
            title="Verify or seed SQLite D1 tables"
          >
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span>Init / Sync DB</span>
          </button>
          <button
            onClick={handleLogout}
            className="px-3.5 py-2 rounded-xl bg-rose-950/60 hover:bg-rose-900/80 text-rose-300 text-xs font-semibold border border-rose-800 flex items-center gap-1.5 transition ml-auto sm:ml-0"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Lock Session</span>
          </button>
        </div>
      </div>

      {feedback && (
        <div
          className={`p-4 rounded-2xl text-xs flex items-center gap-2 border transition ${
            feedback.type === 'success'
              ? 'bg-emerald-950/60 border-emerald-500/30 text-emerald-300'
              : 'bg-rose-950/60 border-rose-500/30 text-rose-300'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex border-b border-slate-800 space-x-2 overflow-x-auto pb-1">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-3 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'overview'
              ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20'
              : 'text-slate-400 hover:text-white hover:bg-slate-900/50'
          }`}
        >
          <Database className="w-4 h-4" />
          <span>Overview</span>
        </button>

        <button
          onClick={() => setActiveTab('games')}
          className={`px-4 py-3 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'games'
              ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20'
              : 'text-slate-400 hover:text-white hover:bg-slate-900/50'
          }`}
        >
          <Gamepad2 className="w-4 h-4" />
          <span>Games ({games.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('staff')}
          className={`px-4 py-3 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'staff'
              ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20'
              : 'text-slate-400 hover:text-white hover:bg-slate-900/50'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Staff ({staff.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('faqs')}
          className={`px-4 py-3 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'faqs'
              ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20'
              : 'text-slate-400 hover:text-white hover:bg-slate-900/50'
          }`}
        >
          <HelpCircle className="w-4 h-4" />
          <span>FAQs ({faqs.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('contacts')}
          className={`px-4 py-3 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'contacts'
              ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20'
              : 'text-slate-400 hover:text-white hover:bg-slate-900/50'
          }`}
        >
          <Mail className="w-4 h-4" />
          <span>Inquiries ({contacts.length})</span>
        </button>
      </div>

      {/* TAB CONTENT: OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 space-y-2">
              <div className="flex items-center justify-between text-cyan-400">
                <span className="text-xs uppercase font-bold tracking-wider text-slate-400">Total Games</span>
                <Gamepad2 className="w-5 h-5" />
              </div>
              <p className="text-3xl font-black text-white">{stats.gamesCount}</p>
              <p className="text-[11px] text-slate-500">Live in catalog</p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 space-y-2">
              <div className="flex items-center justify-between text-purple-400">
                <span className="text-xs uppercase font-bold tracking-wider text-slate-400">Staff Members</span>
                <Users className="w-5 h-5" />
              </div>
              <p className="text-3xl font-black text-white">{stats.staffCount}</p>
              <p className="text-[11px] text-slate-500">Active profiles</p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 space-y-2">
              <div className="flex items-center justify-between text-emerald-400">
                <span className="text-xs uppercase font-bold tracking-wider text-slate-400">Knowledge FAQs</span>
                <HelpCircle className="w-5 h-5" />
              </div>
              <p className="text-3xl font-black text-white">{stats.faqsCount}</p>
              <p className="text-[11px] text-slate-500">Public questions</p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 space-y-2">
              <div className="flex items-center justify-between text-amber-400">
                <span className="text-xs uppercase font-bold tracking-wider text-slate-400">Transmissions</span>
                <Mail className="w-5 h-5" />
              </div>
              <p className="text-3xl font-black text-white">{stats.contactsCount}</p>
              <p className="text-[11px] text-slate-500">User submissions</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-900/40 rounded-3xl border border-slate-800 p-6 space-y-4">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span>Quick Actions</span>
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={openNewGameModal}
                  className="p-4 rounded-xl bg-slate-950/60 hover:bg-slate-800 border border-slate-800 text-left transition space-y-1 group"
                >
                  <Plus className="w-4 h-4 text-cyan-400 group-hover:scale-110 transition-transform" />
                  <p className="text-xs font-bold text-white">Add New Game</p>
                  <p className="text-[10px] text-slate-400">Upload to catalog</p>
                </button>

                <button
                  onClick={openNewStaffModal}
                  className="p-4 rounded-xl bg-slate-950/60 hover:bg-slate-800 border border-slate-800 text-left transition space-y-1 group"
                >
                  <Plus className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform" />
                  <p className="text-xs font-bold text-white">Add Team Member</p>
                  <p className="text-[10px] text-slate-400">Create staff profile</p>
                </button>

                <button
                  onClick={() => setIsFaqModalOpen(true)}
                  className="p-4 rounded-xl bg-slate-950/60 hover:bg-slate-800 border border-slate-800 text-left transition space-y-1 group"
                >
                  <Plus className="w-4 h-4 text-emerald-400 group-hover:scale-110 transition-transform" />
                  <p className="text-xs font-bold text-white">Add FAQ Question</p>
                  <p className="text-[10px] text-slate-400">Support question</p>
                </button>

                <button
                  onClick={() => setActiveTab('contacts')}
                  className="p-4 rounded-xl bg-slate-950/60 hover:bg-slate-800 border border-slate-800 text-left transition space-y-1 group"
                >
                  <Eye className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" />
                  <p className="text-xs font-bold text-white">Review Messages</p>
                  <p className="text-[10px] text-slate-400">Customer feedback</p>
                </button>
              </div>
            </div>

            <div className="bg-slate-900/40 rounded-3xl border border-slate-800 p-6 space-y-4">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Database className="w-4 h-4 text-indigo-400" />
                <span>Architecture Health</span>
              </h3>
              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/50 border border-slate-800">
                  <span className="text-slate-400">Edge Compute Runtime</span>
                  <span className="text-emerald-400 font-mono font-semibold">Cloudflare Pages Functions</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/50 border border-slate-800">
                  <span className="text-slate-400">Database Binding</span>
                  <span className="text-cyan-400 font-mono font-semibold">env.DB (Cloudflare D1)</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/50 border border-slate-800">
                  <span className="text-slate-400">Server Framework</span>
                  <span className="text-amber-400 font-mono font-semibold">Hono v4.7</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: GAMES */}
      {activeTab === 'games' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Manage Games</h2>
            <button
              onClick={openNewGameModal}
              className="px-4 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-md shadow-cyan-500/20 transition cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Add Game</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div
                key={game.id}
                className="bg-slate-900/40 rounded-2xl border border-slate-800 p-5 space-y-4 flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-950">
                    <img
                      src={game.thumbnail_url}
                      alt={game.title}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-950/80 backdrop-blur-md text-cyan-400 border border-slate-800">
                      {game.status}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-white">{game.title}</h3>
                    <p className="text-xs text-cyan-400">{game.genre}</p>
                    <p className="text-xs text-slate-400 line-clamp-2 mt-1">{game.description}</p>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-amber-400">★ {game.rating.toFixed(1)}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditGameModal(game)}
                      className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition"
                      title="Edit Game"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteGame(game)}
                      className="p-2 rounded-lg bg-rose-950/50 hover:bg-rose-900/80 text-rose-300 transition"
                      title="Delete Game"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT: STAFF */}
      {activeTab === 'staff' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Manage Team Members</h2>
            <button
              onClick={openNewStaffModal}
              className="px-4 py-2.5 rounded-xl bg-purple-500 hover:bg-purple-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-md shadow-purple-500/20 transition cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Add Member</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {staff.map((member) => (
              <div
                key={member.id}
                className="bg-slate-900/40 rounded-2xl border border-slate-800 p-5 flex flex-col items-center text-center space-y-3"
              >
                <img
                  src={member.avatar_url}
                  alt={member.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/40"
                />
                <div>
                  <h4 className="text-sm font-bold text-white">{member.name}</h4>
                  <p className="text-[11px] text-cyan-400 uppercase tracking-wider font-semibold">{member.role}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{member.bio}</p>
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-slate-800/80 w-full justify-center">
                  <button
                    onClick={() => openEditStaffModal(member)}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteStaff(member)}
                    className="p-1.5 rounded-lg bg-rose-950/50 hover:bg-rose-900/80 text-rose-300 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT: FAQS */}
      {activeTab === 'faqs' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Manage FAQs</h2>
            <button
              onClick={() => setIsFaqModalOpen(true)}
              className="px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-md shadow-emerald-500/20 transition cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Add FAQ</span>
            </button>
          </div>

          <div className="space-y-3">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-slate-900/40 rounded-2xl border border-slate-800 p-5 flex items-start justify-between gap-4"
              >
                <div className="space-y-1.5">
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-cyan-400">
                    {faq.category}
                  </span>
                  <h4 className="text-sm font-bold text-white">{faq.question}</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{faq.answer}</p>
                </div>
                <button
                  onClick={() => handleDeleteFaq(faq)}
                  className="p-2 rounded-lg bg-rose-950/50 hover:bg-rose-900/80 text-rose-300 shrink-0 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT: CONTACT INQUIRIES */}
      {activeTab === 'contacts' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Incoming Transmissions & Inquiries</h2>
            <span className="text-xs text-slate-400">{contacts.length} messages</span>
          </div>

          {contacts.length === 0 ? (
            <div className="text-center py-16 bg-slate-900/30 rounded-2xl border border-dashed border-slate-800 space-y-2">
              <Mail className="w-8 h-8 text-slate-600 mx-auto" />
              <p className="text-sm font-semibold text-white">No incoming transmissions yet</p>
              <p className="text-xs text-slate-500">Submissions from /contact will be recorded here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {contacts.map((msg) => (
                <div
                  key={msg.id}
                  className="bg-slate-900/40 rounded-2xl border border-slate-800 p-6 space-y-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800/80 pb-3">
                    <div>
                      <h4 className="text-sm font-bold text-white">{msg.subject || 'General Inquiry'}</h4>
                      <p className="text-xs text-slate-400">
                        From: <span className="text-cyan-400">{msg.name}</span> ({msg.email})
                      </p>
                    </div>
                    <span className="text-[11px] font-mono text-slate-500">
                      {msg.created_at ? new Date(msg.created_at).toLocaleString() : 'Recent'}
                    </span>
                  </div>
                  <p className="text-slate-300 text-xs leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* GAME MODAL (ADD / EDIT) */}
      <Modal
        isOpen={isGameModalOpen}
        onClose={() => setIsGameModalOpen(false)}
        title={editingGame ? `Edit Game: ${editingGame.title}` : 'Upload / Add New Game'}
      >
        <form onSubmit={handleSaveGame} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Title</label>
              <input
                type="text"
                required
                placeholder="Game Name"
                value={gameForm.title}
                onChange={(e) => setGameForm({ ...gameForm, title: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Slug</label>
              <input
                type="text"
                placeholder="e.g. neon-odyssey"
                value={gameForm.slug}
                onChange={(e) => setGameForm({ ...gameForm, slug: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Genre</label>
              <input
                type="text"
                required
                value={gameForm.genre}
                onChange={(e) => setGameForm({ ...gameForm, genre: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Status</label>
              <select
                value={gameForm.status}
                onChange={(e) => setGameForm({ ...gameForm, status: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              >
                <option value="Released">Released</option>
                <option value="Early Access">Early Access</option>
                <option value="In Development">In Development</option>
                <option value="Beta">Beta</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Rating (0 - 5)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={gameForm.rating}
                onChange={(e) => setGameForm({ ...gameForm, rating: parseFloat(e.target.value) || 5.0 })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-300">Description</label>
            <textarea
              rows={3}
              required
              value={gameForm.description}
              onChange={(e) => setGameForm({ ...gameForm, description: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Thumbnail Image URL</label>
              <input
                type="url"
                required
                value={gameForm.thumbnail_url}
                onChange={(e) => setGameForm({ ...gameForm, thumbnail_url: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 font-mono"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Banner Image URL</label>
              <input
                type="url"
                value={gameForm.banner_url}
                onChange={(e) => setGameForm({ ...gameForm, banner_url: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Play / Launch URL</label>
              <input
                type="text"
                placeholder="https://store.steampowered.com/..."
                value={gameForm.play_url}
                onChange={(e) => setGameForm({ ...gameForm, play_url: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 font-mono"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Store / Download URL</label>
              <input
                type="text"
                placeholder="https://store.epicgames.com/..."
                value={gameForm.download_url}
                onChange={(e) => setGameForm({ ...gameForm, download_url: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 font-mono"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-300">Tags (comma-separated)</label>
            <input
              type="text"
              placeholder="Cyberpunk, Multiplayer, Ray Tracing"
              value={gameForm.tags}
              onChange={(e) => setGameForm({ ...gameForm, tags: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="featuredCheck"
              checked={gameForm.featured}
              onChange={(e) => setGameForm({ ...gameForm, featured: e.target.checked })}
              className="w-4 h-4 rounded text-cyan-500 focus:ring-0 bg-slate-950 border-slate-800 cursor-pointer"
            />
            <label htmlFor="featuredCheck" className="text-xs text-slate-300 font-semibold cursor-pointer">
              Pin as Featured Title on Landing Page
            </label>
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsGameModalOpen(false)}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold transition flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{editingGame ? 'Update Game' : 'Save & Publish'}</span>
            </button>
          </div>
        </form>
      </Modal>

      {/* STAFF MODAL (ADD / EDIT) */}
      <Modal
        isOpen={isStaffModalOpen}
        onClose={() => setIsStaffModalOpen(false)}
        title={editingStaff ? `Edit Staff: ${editingStaff.name}` : 'Add Staff Member'}
      >
        <form onSubmit={handleSaveStaff} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Name</label>
              <input
                type="text"
                required
                placeholder="e.g. Satoshi Nakamoto"
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Role</label>
              <input
                type="text"
                required
                placeholder="e.g. Lead Graphics Engineer"
                value={staffForm.role}
                onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-300">Avatar URL</label>
            <input
              type="url"
              required
              value={staffForm.avatar_url}
              onChange={(e) => setStaffForm({ ...staffForm, avatar_url: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 font-mono"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-300">Bio</label>
            <textarea
              rows={3}
              value={staffForm.bio}
              onChange={(e) => setStaffForm({ ...staffForm, bio: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">Twitter URL</label>
              <input
                type="text"
                placeholder="https://x.com/..."
                value={staffForm.twitter}
                onChange={(e) => setStaffForm({ ...staffForm, twitter: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">GitHub URL</label>
              <input
                type="text"
                placeholder="https://github.com/..."
                value={staffForm.github}
                onChange={(e) => setStaffForm({ ...staffForm, github: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-300">LinkedIn URL</label>
              <input
                type="text"
                placeholder="https://linkedin.com/in/..."
                value={staffForm.linkedin}
                onChange={(e) => setStaffForm({ ...staffForm, linkedin: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsStaffModalOpen(false)}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-purple-500 hover:bg-purple-400 text-slate-950 text-xs font-bold transition flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{editingStaff ? 'Update Profile' : 'Save Member'}</span>
            </button>
          </div>
        </form>
      </Modal>

      {/* FAQ MODAL */}
      <Modal
        isOpen={isFaqModalOpen}
        onClose={() => setIsFaqModalOpen(false)}
        title="Add FAQ Question"
      >
        <form onSubmit={handleSaveFaq} className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-300">Category</label>
            <select
              value={faqForm.category}
              onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
            >
              <option value="General">General</option>
              <option value="Development">Development</option>
              <option value="Careers">Careers</option>
              <option value="Publishing">Publishing</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-300">Question</label>
            <input
              type="text"
              required
              placeholder="e.g. What platforms do GSR games support?"
              value={faqForm.question}
              onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-300">Answer</label>
            <textarea
              rows={4}
              required
              placeholder="Provide a thorough, informative answer..."
              value={faqForm.answer}
              onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsFaqModalOpen(false)}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold transition flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              <span>Save Question</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
